import { type EditorState, Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet, type EditorView } from "@tiptap/pm/view";
import { alerts } from "app/stores/alert";
import { uploadFileToUploadcare } from "app/utils/uploadcare";
import cx from "classnames";

const uploadKey = new PluginKey("upload-file");

/** On an add/remove transaction, add/remove a placeholder decoration.
 *
 * Note: Because this is a decoration this isn't stored in the doc or
 * shared across users in a session.
 *
 */
const FileUploadPlugin = () =>
  new Plugin({
    key: uploadKey,
    state: {
      init() {
        return DecorationSet.empty;
      },
      apply(tr, set) {
        set = set.map(tr.mapping, tr.doc);
        // See if the transaction adds or removes any placeholders
        const action = tr.getMeta(this as any);
        if (action && action.add) {
          const { id, pos, src, type } = action.add;

          const placeholder = document.createElement("div");
          placeholder.setAttribute("class", "flex w-full");

          if (type.includes("image")) {
            const frame = document.createElement("div");
            frame.setAttribute("class", "img-placeholder relative basis-3/4 md:basis-1/4 -my-2");
            placeholder.appendChild(frame);
            const image = document.createElement("img");
            image.setAttribute("class", "w-full opacity-40 rounded-lg object-cover");
            image.src = src;
            frame.appendChild(image);
          } else {
            // create a placeholder box
            const box = document.createElement("div");
            box.setAttribute(
              "class",
              cx(
                type.includes("video") ? "video-placeholder h-40" : "file-placeholder h-14",
                "in-out-shadow my-1 flex basis-full cursor-pointer items-center gap-1.5 rounded-md bg-black/2 px-2 py-2.5 after:rounded-md sm:basis-1/2 dark:bg-white/3"
              )
            );

            placeholder.appendChild(box);
          }

          // add placeholder as decoration in transaction
          const deco = Decoration.widget(pos + 1, placeholder, { id });
          set = set.add(tr.doc, [deco]);
        } else if (action && action.remove) {
          // find and remove the placeholder in transaction
          set = set.remove(
            set.find(null as any, null as any, (spec) => spec.id == action.remove.id)
          );
        }
        return set;
      },
    },
    props: {
      decorations(state) {
        return this.getState(state);
      },
    },
  });

export default FileUploadPlugin;

/** Find the placeholder decoration with the given object ID */
function findPlaceholder(state: EditorState, id: any) {
  const decos = uploadKey.getState(state);
  const found = decos.find(null, null, (spec: any) => spec.id == id);
  return found.length ? found[0].from : null;
}

/** a simple promise that resolves when the img has loaded */
const waitForLoad = (img: HTMLImageElement) => new Promise((resolve) => (img.onload = resolve));

/**
 * Inserts a file into the editor, first as a placeholder, then the file/image
 * @param file the file to upload
 * @param view the editor view
 * @param pos the position to insert the placeholder
 * @returns
 */
export async function startUpload(file: File, view: EditorView, pos: number, metadata: any) {
  const fileType = file.type || file.name.split(".").pop() || "";

  // restrict image size to 20MB
  if (fileType.includes("image") && file.size / 1000 / 1000 > 20) {
    alerts.error(`${file.name} size too big (max 20MB).`);
    return;
    // check if the file size is less than 1GB
  } else if (file.size / 1000 / 1000 > 1000) {
    alerts.error(`${file.name} size too big (max 1GB).`);
    return;
  }

  // A fresh object to act as the ID for this upload
  const id = {};

  // Replace the selection with a placeholder
  const tr = view.state.tr;
  if (!tr.selection.empty) tr.deleteSelection();

  // only open image files
  if (fileType.includes("image")) {
    // get data url of image
    const reader = new FileReader();
    reader.onload = () => {
      // request an image placeholder with the data url
      tr.setMeta(uploadKey, {
        add: {
          id,
          pos,
          src: reader.result,
          type: fileType,
          fileName: file.name,
        },
      });
      view.dispatch(tr);
    };
    reader.readAsDataURL(file);
  } else {
    // request a simple placeholder
    tr.setMeta(uploadKey, {
      add: {
        id,
        pos,
        src: "",
        type: fileType,
        fileName: file.name,
      },
    });
    view.dispatch(tr);
  }

  const ucareFile = await handleUpload(file, fileType, metadata);
  const { schema } = view.state;

  const placeholderPos = findPlaceholder(view.state, id);
  // If the content around the placeholder has been deleted, drop the image
  if (placeholderPos == null) return;

  // Otherwise, insert it at the placeholder's position, and remove the placeholder
  // create node based on file type
  let node;
  const props = {
    src: ucareFile.src,
    uuid: ucareFile.uuid,
    type: fileType,
    fileSize: file.size,
    fileName: file.name,
    width: "SMALL",
  };
  if (fileType.includes("image")) {
    node = schema.nodes.image.create(props);
  } else if (fileType.includes("video")) {
    node = schema.nodes.video.create(props);
  } else {
    node = schema.nodes.file.create(props);
  }

  // replace placeholder with node
  const transaction = view.state.tr
    .replaceWith(pos, pos, node)
    .setMeta(uploadKey, { remove: { id } });
  view.dispatch(transaction);
}

export const handleUpload = async (file: File, fileType: string, metadata: any) => {
  const isImage = fileType.includes("image");
  const ucareFile = await uploadFileToUploadcare(file, isImage, metadata);
  // preload image to avoid flash in-out when swapping between placeholder and image
  if (fileType.includes("image")) {
    const image = document.createElement("img");
    image.srcset = [200, 400, 500, 800, 1000, 1600]
      .map(
        (width) => `${ucareFile.src}-/resize/${width}x/-/format/auto/-/quality/better/ ${width}w`
      )
      .join(",");
    image.src = ucareFile.src;
    // wait for image to be pre-loaded before proceeding
    await waitForLoad(image);
    image.remove();
  }
  return ucareFile;
};
